import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import { SearchInput } from "@/pages/projekte/SearchInput";
import { stripGdRichTextMarkup } from "@/lib/gdRichText";
import { isDocumentNumberQuery } from "@/lib/searchQuery";
import { listOrdersWithTotal } from "@/lib/projects";
import {
  DOKUMENT_STATUS_LABELS,
  DOKUMENT_TYP_LABELS,
  dokumentDisplayNumber,
  fetchDokumentPdf,
  fetchHeroDokumentPdf,
  getDokumentTypLabel,
  isHeroCustomerDocumentListItem,
  isHeroOriginDokument,
  listDokumenteWithTotal,
  type DokumentListItem,
  type DokumentStatus,
  type DokumentTyp,
} from "@/lib/dokumente";

const PAGE_SIZE = 50;
const PROJECT_SEARCH_LIMIT = 100;
const PICKER_TYPEN: DokumentTyp[] = [
  "angebot",
  "auftragsbestaetigung",
  "lieferschein",
  "rechnung",
];

const PICKER_TYP_SET = new Set<DokumentTyp>(PICKER_TYPEN);

const STATUS_OPTIONS: { value: DokumentStatus | ""; label: string }[] = [
  { value: "", label: "Alle Status" },
  { value: "entwurf", label: "Entwurf" },
  { value: "erstellt", label: "Erstellt" },
  { value: "versendet", label: "Versendet" },
  { value: "angenommen", label: "Angenommen" },
  { value: "abgelehnt", label: "Abgelehnt" },
  { value: "geliefert", label: "Geliefert" },
  { value: "abgerechnet", label: "Bezahlt" },
  { value: "storniert", label: "Storniert" },
];

function docKey(doc: DokumentListItem): string {
  if (isHeroCustomerDocumentListItem(doc)) {
    return `hero-${doc.hero_document_id ?? doc.id}`;
  }
  return `gd-${doc.id}`;
}

function isPickerEligible(doc: DokumentListItem): boolean {
  return PICKER_TYP_SET.has(doc.dokument_typ);
}

function betreffPlain(doc: DokumentListItem): string {
  return stripGdRichTextMarkup((doc.betreff || "").trim());
}

function projectTitle(doc: DokumentListItem): string {
  const beschreibung = (doc.auftrag_beschreibung || "").trim();
  if (beschreibung) return beschreibung;
  if (doc.auftrag_nummer) return `Projekt ${doc.auftrag_nummer}`;
  return "Ohne Projekt";
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("de-DE");
}

function formatMoney(value: number, currency: string): string {
  if (!Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(value);
  } catch {
    return `${value.toLocaleString("de-DE", { minimumFractionDigits: 2 })} ${currency || "€"}`;
  }
}

function SourcePdfPreview({ doc }: { doc: DokumentListItem }) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const heroId = Number(doc.hero_document_id ?? 0);
  const isHero = isHeroCustomerDocumentListItem(doc);
  const appId = Number(doc.id);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlob(null);

    const load = isHero
      ? heroId > 0
        ? fetchHeroDokumentPdf(heroId)
        : Promise.reject(new Error("Hero-Dokument ohne gültige ID."))
      : appId > 0
        ? fetchDokumentPdf(appId)
        : Promise.reject(new Error("Dokument ohne gültige ID."));

    void load
      .then((next) => {
        if (cancelled) return;
        setBlob(next.type === "application/pdf" ? next : new Blob([next], { type: "application/pdf" }));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "PDF konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appId, heroId, isHero]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-label-secondary">
        <Spinner className="h-4 w-4" />
        PDF wird geladen …
      </div>
    );
  }
  if (error) {
    return (
      <p className="flex h-full items-center justify-center px-4 text-center text-sm text-status-red">
        {error}
      </p>
    );
  }
  if (!blob) return null;

  return (
    <InlinePdfView
      blob={blob}
      title="Dokumentvorschau"
      className="absolute inset-0 h-full w-full"
    />
  );
}

export interface DocumentSourcePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (doc: DokumentListItem) => void;
  /** Wenn gesetzt: Schalter „Nur aktuelles Projekt“ möglich. */
  auftragId?: number | null;
  /** Vorausgewählter Typ-Filter (z. B. aktueller Create-Typ). */
  initialTyp?: DokumentTyp | "";
  /** Bereits gewähltes Dokument – wird beim Öffnen hervorgehoben. */
  selectedDoc?: DokumentListItem | null;
  zIndexClass?: string;
}

export function DocumentSourcePickerDialog({
  open,
  onClose,
  onSelect,
  auftragId = null,
  initialTyp = "",
  selectedDoc = null,
  zIndexClass = "z-[70]",
}: DocumentSourcePickerDialogProps) {
  const [query, setQuery] = useState("");
  const [typ, setTyp] = useState<DokumentTyp | "">("");
  const [status, setStatus] = useState<DokumentStatus | "">("");
  const [onlyCurrentProject, setOnlyCurrentProject] = useState(false);
  const [items, setItems] = useState<DokumentListItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState<DokumentListItem | null>(null);
  const requestSeqRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  /** Auftrag-IDs der letzten hybriden Projektsuche – für „Weitere laden“. */
  const projectIdsRef = useRef<number[] | null>(null);

  const highlightedKey = highlighted ? docKey(highlighted) : null;
  const canFilterByProject = auftragId != null && Number.isFinite(auftragId) && auftragId > 0;

  useEffect(() => {
    if (!open) return;
    const startTyp =
      initialTyp && PICKER_TYP_SET.has(initialTyp as DokumentTyp) ? (initialTyp as DokumentTyp) : "";
    setQuery("");
    setTyp(startTyp);
    setStatus("");
    setOnlyCurrentProject(false);
    setItems([]);
    setTotalCount(null);
    setNextOffset(0);
    setError(null);
    setHighlighted(selectedDoc);
    projectIdsRef.current = null;
  }, [open, initialTyp, selectedDoc]);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const loadPage = useCallback(
    async (offset: number, append: boolean) => {
      const requestId = ++requestSeqRef.current;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      try {
        const q = query.trim();
        const currentAuftragId =
          onlyCurrentProject && canFilterByProject ? Number(auftragId) : undefined;

        let auftragIds: number[] | undefined;
        let useDocumentTextSearch = false;

        if (append) {
          auftragIds = projectIdsRef.current ?? undefined;
          useDocumentTextSearch = projectIdsRef.current === null && Boolean(q) && !currentAuftragId;
        } else if (q && !currentAuftragId && isDocumentNumberQuery(q)) {
          // Angebots-/Belegnummer direkt in den Dokumentfeldern suchen.
          projectIdsRef.current = null;
          useDocumentTextSearch = true;
        } else if (q && !currentAuftragId) {
          // Gleiche hybride Projektsuche wie auf der Seite Projekte.
          const { orders } = await listOrdersWithTotal(
            {
              q,
              limit: PROJECT_SEARCH_LIMIT,
              offset: 0,
              include_hero_custom_fields: false,
              include_tags: false,
              include_timesheets: false,
              include_hero_steps: false,
            },
            { signal: controller.signal },
          );
          if (requestId !== requestSeqRef.current) return;
          const ids = orders
            .map((order) => Number(order.id))
            .filter((id) => Number.isFinite(id) && id > 0);
          if (ids.length > 0) {
            auftragIds = ids;
            projectIdsRef.current = ids;
          } else {
            // Kein Projekt-Treffer: Fallback auf Dokumentfelder (Nummer, Betreff, …).
            projectIdsRef.current = null;
            useDocumentTextSearch = true;
          }
        } else {
          projectIdsRef.current = null;
        }

        const result = await listDokumenteWithTotal(
          {
            q: useDocumentTextSearch || Boolean(currentAuftragId && q) ? q || undefined : undefined,
            typ: typ || undefined,
            status: status || undefined,
            auftrag_id: currentAuftragId,
            auftrag_ids: !currentAuftragId ? auftragIds : undefined,
            limit: PAGE_SIZE,
            offset,
            sort: "belegdatum",
            direction: "desc",
          },
          { signal: controller.signal },
        );
        if (requestId !== requestSeqRef.current) return;

        let next = (typ ? result.items : result.items.filter(isPickerEligible)).filter(
          (doc) => !isHeroCustomerDocumentListItem(doc) || Number(doc.hero_document_id ?? 0) > 0,
        );

        // Treffer der Projektsuche nach Relevanz der Projekte sortieren.
        if (auftragIds && auftragIds.length > 0) {
          const orderIndex = new Map(auftragIds.map((id, index) => [id, index]));
          next = [...next].sort((a, b) => {
            const ia = orderIndex.get(Number(a.auftrag_id)) ?? Number.MAX_SAFE_INTEGER;
            const ib = orderIndex.get(Number(b.auftrag_id)) ?? Number.MAX_SAFE_INTEGER;
            if (ia !== ib) return ia - ib;
            const da = a.belegdatum || "";
            const db = b.belegdatum || "";
            return db.localeCompare(da);
          });
        }

        setItems((prev) => (append ? [...prev, ...next] : next));
        setTotalCount(result.totalCount);
        setNextOffset(offset + result.items.length);
      } catch (err: unknown) {
        if (
          (err instanceof DOMException || err instanceof Error) &&
          err.name === "AbortError"
        ) {
          return;
        }
        if (requestId !== requestSeqRef.current) return;
        if (!append) {
          setItems([]);
          setTotalCount(null);
          setNextOffset(0);
          projectIdsRef.current = null;
        }
        setError(err instanceof Error ? err.message : "Dokumente konnten nicht geladen werden.");
      } finally {
        if (requestId === requestSeqRef.current) {
          if (append) setLoadingMore(false);
          else setLoading(false);
        }
      }
    },
    [query, typ, status, onlyCurrentProject, canFilterByProject, auftragId],
  );

  useEffect(() => {
    if (!open) return;
    void loadPage(0, false);
  }, [open, loadPage]);

  const hasMore = useMemo(() => {
    if (totalCount == null) return nextOffset > 0 && nextOffset % PAGE_SIZE === 0;
    return nextOffset < totalCount;
  }, [nextOffset, totalCount]);

  const resultLabel = useMemo(() => {
    if (loading && items.length === 0) return "Suche …";
    if (totalCount != null) {
      return `${items.length} von ${totalCount.toLocaleString("de-DE")} Dokumenten`;
    }
    return `${items.length.toLocaleString("de-DE")} Dokumente`;
  }, [loading, items.length, totalCount]);

  function handleConfirm() {
    if (!highlighted) return;
    onSelect(highlighted);
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Dokument zum Kopieren wählen"
      description="Über die Projektsuche finden, in der Vorschau prüfen und dann übernehmen."
      size="4xl"
      fullScreenMobile
      plainBackdrop
      zIndexClass={zIndexClass}
      bodyClassName="!flex !min-h-0 !flex-1 !flex-col !overflow-hidden !p-0 max-sm:!overflow-hidden"
      panelClassName="h-[min(92vh,56rem)] max-h-[min(92vh,56rem)]"
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-label-secondary">{resultLabel}</p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="button" disabled={!highlighted} onClick={handleConfirm}>
              Dieses Dokument verwenden
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
        <div className="flex h-full min-h-0 w-full flex-col border-b border-separator lg:w-[22rem] lg:shrink-0 lg:border-b-0 lg:border-r xl:w-[26rem]">
          <div className="shrink-0 space-y-3 border-b border-separator px-4 py-3">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Projekte oder Angebotsnummer durchsuchen …"
              debounceMs={300}
            />
            <div className="grid grid-cols-2 gap-2">
              <label className="block space-y-1 text-xs">
                <span className="text-label-secondary">Typ</span>
                <select
                  value={typ}
                  onChange={(e) => setTyp(e.target.value as DokumentTyp | "")}
                  className="eh-input w-full text-sm"
                >
                  <option value="">Alle Typen</option>
                  {PICKER_TYPEN.map((t) => (
                    <option key={t} value={t}>
                      {DOKUMENT_TYP_LABELS[t]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block space-y-1 text-xs">
                <span className="text-label-secondary">Status</span>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as DokumentStatus | "")}
                  className="eh-input w-full text-sm"
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value || "all"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {canFilterByProject ? (
              <Checkbox
                label="Nur aktuelles Projekt"
                checked={onlyCurrentProject}
                onChange={(e) => setOnlyCurrentProject(e.target.checked)}
              />
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {loading && items.length === 0 ? (
              <div className="flex items-center justify-center gap-2 py-16 text-sm text-label-secondary">
                <Spinner className="h-4 w-4" />
                Dokumente werden geladen …
              </div>
            ) : error && items.length === 0 ? (
              <p className="px-4 py-16 text-center text-sm text-status-red">{error}</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-16 text-center text-sm text-label-tertiary">
                Keine Dokumente gefunden. Suche oder Filter anpassen.
              </p>
            ) : (
              <ul className="divide-y divide-separator">
                {items.map((doc) => {
                  const key = docKey(doc);
                  const active = key === highlightedKey;
                  const betreff = betreffPlain(doc);
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => setHighlighted(doc)}
                        className={clsx(
                          "w-full px-4 py-3 text-left transition",
                          active
                            ? "bg-accent-soft/80 ring-1 ring-inset ring-accent/30"
                            : "hover:bg-fill-tertiary/70",
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="min-w-0 text-sm font-semibold text-label">
                            {projectTitle(doc)}
                          </p>
                          <span className="shrink-0 text-[11px] text-label-tertiary">
                            {formatDate(doc.belegdatum)}
                          </span>
                        </div>
                        <p className="mt-0.5 truncate font-mono text-xs text-label-secondary">
                          {doc.auftrag_nummer ? doc.auftrag_nummer : "Ohne Projektnummer"}
                          {doc.kunde_name ? ` · ${doc.kunde_name}` : ""}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-label-tertiary">
                          {getDokumentTypLabel(doc)} {dokumentDisplayNumber(doc)}
                          {" · "}
                          {DOKUMENT_STATUS_LABELS[doc.status] || doc.status}
                          {isHeroOriginDokument(doc) ? " · Hero" : ""}
                          {betreff ? ` · ${betreff}` : ""}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {hasMore && items.length > 0 ? (
              <div className="border-t border-separator p-3">
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full"
                  disabled={loadingMore}
                  loading={loadingMore}
                  onClick={() => void loadPage(nextOffset, true)}
                >
                  Weitere laden
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-fill-quaternary/20 max-lg:min-h-[22rem]">
          {!highlighted ? (
            <div className="flex flex-1 items-center justify-center px-6 text-center text-sm text-label-secondary">
              Ein Dokument in der Liste auswählen, um die Vorschau zu prüfen.
            </div>
          ) : (
            <>
              <div className="shrink-0 space-y-1 border-b border-separator bg-surface px-4 py-3">
                <h3 className="text-sm font-semibold text-label">{projectTitle(highlighted)}</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-label-secondary">
                    {highlighted.auftrag_nummer
                      ? highlighted.auftrag_nummer
                      : "Ohne Projektnummer"}
                  </span>
                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                    {getDokumentTypLabel(highlighted)} {dokumentDisplayNumber(highlighted)}
                  </span>
                  {isHeroOriginDokument(highlighted) ? (
                    <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                      Hero
                    </span>
                  ) : null}
                  <span className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary">
                    {DOKUMENT_STATUS_LABELS[highlighted.status] || highlighted.status}
                  </span>
                </div>
                <p className="text-xs text-label-secondary">
                  {[
                    highlighted.kunde_name || null,
                    highlighted.belegdatum
                      ? `Belegdatum ${formatDate(highlighted.belegdatum)}`
                      : null,
                    formatMoney(Number(highlighted.summe_brutto), highlighted.waehrung),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                {betreffPlain(highlighted) ? (
                  <p className="line-clamp-2 text-xs text-label-tertiary">
                    {betreffPlain(highlighted)}
                  </p>
                ) : null}
              </div>
              <div className="relative min-h-0 flex-1 overflow-hidden">
                <SourcePdfPreview key={docKey(highlighted)} doc={highlighted} />
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
